import React, { useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ErrorState, PageHeader } from '../../components/app';
import { Percent, Plus, Scale } from 'lucide-react';
import { useFounders } from './hooks/useFounders';
import {
  BalanceCheckDialog,
  BalanceChecksTable,
  FounderCards,
  SharesDialog,
  TransactionDialog,
  TransactionsTable,
  TreasurySummary,
} from './components';
import { FoundersTab } from './types';

/** Вкладки нижнего блока: движения денег и сверка кассы с банком. */
const TABS: Array<{ key: FoundersTab; title: string; hint: string }> = [
  {
    key: 'transactions',
    title: 'Движения',
    hint: 'Взносы на счёт, возвраты вкладов и доли прибыли. Расходы из кармана сюда не попадают — они уже учтены на экране «Расходы»',
  },
  {
    key: 'checks',
    title: 'Остатки по выписке',
    hint: 'Сколько денег реально было на счету на дату. Расхождение с Кассой означает забытый Расход или Движение',
  },
];

const NUMBER = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
const formatMoney = (amount: number) => `${NUMBER.format(amount)} сум`;

export default function FoundersPage() {
  const {
    overview,
    payers,
    isLoading,
    loadError,
    refresh,
    isSubmitting,
    isTransactionDialogOpen,
    setIsTransactionDialogOpen,
    isSharesDialogOpen,
    setIsSharesDialogOpen,
    isBalanceDialogOpen,
    setIsBalanceDialogOpen,
    editingTransaction,
    transactionForm,
    setTransactionForm,
    balanceForm,
    setBalanceForm,
    openCreateTransaction,
    openEditTransaction,
    submitTransaction,
    deleteTransaction,
    openBalanceCheck,
    submitBalanceCheck,
    deleteBalanceCheck,
    updateShare,
  } = useFounders();

  const [tab, setTab] = useState<FoundersTab>('transactions');

  const founderPayers = useMemo(() => payers.filter((p) => !p.is_company), [payers]);

  const debtByFounder = useMemo(
    () => Object.fromEntries((overview?.founders || []).map((f) => [f.payerId, f.debt])) as Record<string, number>,
    [overview],
  );

  const profitAvailableByFounder = useMemo(
    () =>
      Object.fromEntries((overview?.founders || []).map((f) => [f.payerId, f.profitAvailable])) as Record<
        string,
        number
      >,
    [overview],
  );

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Основатели"
        subtitle="Кто сколько вложил, сколько компания должна вернуть и что можно делить"
        actions={
          <>
            <Button variant="secondary" onClick={() => setIsSharesDialogOpen(true)}>
              <Percent aria-hidden />
              Доли в прибыли
            </Button>
            <Button variant="secondary" onClick={openBalanceCheck}>
              <Scale aria-hidden />
              Записать остаток
            </Button>
            <Button onClick={openCreateTransaction}>
              <Plus aria-hidden />
              Добавить движение
            </Button>
          </>
        }
      />

      {loadError ? (
        <Card>
          <ErrorState title="Не удалось загрузить Казну" message={loadError} onRetry={refresh} />
        </Card>
      ) : (
        <>
          <TreasurySummary overview={overview} isLoading={isLoading} formatMoney={formatMoney} />

          <FounderCards founders={overview?.founders || []} isLoading={isLoading} formatMoney={formatMoney} />

          <Card className="overflow-hidden">
            <Tabs value={tab} onValueChange={(value) => setTab(value as FoundersTab)}>
              <div className="px-[18px] pt-3">
                <TabsList variant="underline" className="border-b-0">
                  {TABS.map((item) => (
                    <TabsTrigger key={item.key} value={item.key}>
                      {item.title}
                      {overview && (
                        <span className="tabular-nums text-text-2">
                          {item.key === 'transactions' ? overview.transactions.length : overview.checks.length}
                        </span>
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              <p className="m-0 border-t border-border-soft px-[18px] py-3 text-[13px] text-text-2">
                {TABS.find((item) => item.key === tab)?.hint}
              </p>
              <TabsContent value="transactions" className="mt-0">
                <TransactionsTable
                  transactions={overview?.transactions || []}
                  isLoading={isLoading}
                  onEdit={openEditTransaction}
                  onDelete={deleteTransaction}
                  formatMoney={formatMoney}
                />
              </TabsContent>
              <TabsContent value="checks" className="mt-0">
                <BalanceChecksTable
                  checks={overview?.checks || []}
                  isLoading={isLoading}
                  onDelete={deleteBalanceCheck}
                  formatMoney={formatMoney}
                />
              </TabsContent>
            </Tabs>
          </Card>
        </>
      )}

      <TransactionDialog
        isOpen={isTransactionDialogOpen}
        onOpenChange={setIsTransactionDialogOpen}
        founders={founderPayers}
        formData={transactionForm}
        onFormDataChange={setTransactionForm}
        onSubmit={submitTransaction}
        isEditing={Boolean(editingTransaction)}
        isSubmitting={isSubmitting}
        debtByFounder={debtByFounder}
        profitAvailableByFounder={profitAvailableByFounder}
        formatMoney={formatMoney}
      />

      <SharesDialog
        isOpen={isSharesDialogOpen}
        onOpenChange={setIsSharesDialogOpen}
        founders={founderPayers}
        onUpdate={updateShare}
      />

      <BalanceCheckDialog
        isOpen={isBalanceDialogOpen}
        onOpenChange={setIsBalanceDialogOpen}
        formData={balanceForm}
        onFormDataChange={setBalanceForm}
        onSubmit={submitBalanceCheck}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
